import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FAQ from "../components/FAQs";
import SEO from "../components/SEO";
import { DEMO_LINK } from "../components/demoBooking";

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();

  const doScrollHomeDemo = (e) => {
    e.preventDefault();
    const doScroll = () =>
      document
        .querySelector("#demo")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(doScroll, 0);
    } else {
      doScroll();
    }
  };

  const tiers = [
    {
      name: "Pilot",
      price: "Let's talk",
      blurb: "45 days, one team. Automated from day 15, every action logged.",
      cta: "Book a pilot call",
      booking: true,
      pilot: true,
      features: [
        "One team, fully instrumented",
        "Two-week baseline so the change is measurable",
        "Everything in Team",
        "Direct founder support throughout",
        "A day-45 readout you can share",
      ],
    },
    {
      name: "Team",
      price: "Talk to us",
      blurb: "One team, one operating cadence.",
      cta: "Book a demo",
      booking: true,
      featured: true,
      features: [
        "Dedicated server",
        "Weekly executive report",
        "Auto-created & updated tasks",
        "Auto-scheduled calendar meetings",
        "Auto DMs for escalations & status updates",
        "Risk detection",
        "Approval queue & exportable audit log",
      ],
    },
    {
      name: "Org",
      price: "Talk to us",
      blurb: "Multi-team rollups and controls",
      cta: "Contact sales",
      href: "/contact-sales",
      features: [
        "Custom automations",
        "Custom polling rate",
        "Cross-team reporting",
        "Advanced permissions",
        "Priority support",
      ],
    },
  ];

  return (
    <main className="page">
      <SEO
        title="BossDrop | Pricing"
        description="BossDrop is priced per team, not per seat. Start with a 45-day pilot on one team: two weeks of watching, then it runs on its own, with every action logged."
        canonical="https://bossdrop.ai/pricing"
        url="https://bossdrop.ai/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
          offers: [
            {
              "@type": "Offer",
              name: "Team",
              url: "https://bossdrop.ai/pricing",
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Org",
              url: "https://bossdrop.ai/contact-sales",
              availability: "https://schema.org/PreOrder",
            },
          ],
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Pricing",
              item: "https://bossdrop.ai/pricing",
            },
          ],
        }}
      />

      <section className="hero">
        <h1 className="h1">Pricing</h1>
        <p className="sub">
          Priced <strong>per team, not per seat</strong>. The point is
          fewer coordinators, not more logins. Start with one team for 45 days
          and measure it against a two-week baseline.
        </p>
      </section>

      <section className="grid pricing">
        {tiers.map((t, i) => (
          <article
            className={`card price ${t.featured ? "featured" : ""}`}
            key={i}
          >
            <div className="priceHeader">
              <h3>{t.name}</h3>
              <div className="priceTag">{t.price}</div>
              <p className="muted">{t.blurb}</p>
            </div>

            <ul className="bullets">
              {(t.features || []).map((f, j) => (
                <li key={j}>{f}</li>
              ))}
            </ul>

            <a
              className={`btn ${
                t.pilot ? "btnCream" : t.featured ? "btnPrimary" : "btnGhost"
              }`}
              {...(t.booking
                ? DEMO_LINK
                : { href: t.href, onClick: t.href === "/" ? doScrollHomeDemo : undefined })}
            >
              {t.cta}
            </a>
          </article>
        ))}
      </section>

      <FAQ
        items={[
          {
            q: "Why isn't pricing listed?",
            a: "Because the right number depends on how many teams you run and what shape your stack is in, and we would rather quote you accurately than anchor you wrongly. Every pilot conversation ends with a specific number and a specific scope.",
          },
          {
            q: "Why per team instead of per seat?",
            a: "Per-seat pricing charges you more as headcount grows, which is backwards for a product whose whole point is that coordination stops being a headcount line. BossDrop runs one operating cadence per team, so the team is the unit you pay for.",
          },
          {
            q: "What does BossDrop automate today?",
            a: "Creating and updating tasks, scheduling decision meetings, chasing status updates, asking clarifying questions, risk detection and escalation, replying to email threads, and the weekly executive report.",
          },
          {
            q: "Which tools do you integrate with?",
            a: "Slack, Microsoft Teams, Gmail and Outlook, Asana, Jira, Linear, ClickUp, Notion, monday.com, Microsoft Planner, GitHub, Google and Outlook Calendar, HubSpot, and Pipedrive.",
          },
          {
            q: "How long does setup take?",
            a: "Usually minutes. Connect your tools, set scope and ownership in the Control Room, and automations start on your green light, with approvals where they matter.",
          },
        ]}
      />
    </main>
  );
}
